import { Fragment, useId, useRef, useState, useSyncExternalStore } from 'react';
import styles from './Passions.module.css';
import { site } from '../content/site.js';
import { useStandings } from '../hooks/useStandings.js';
import Reveal from './Reveal.jsx';

const TOP = 10;
const STAGES = {
  PLAYOFFS: 'Knockout play-off',
  LAST_16: 'Round of 16',
  QUARTER_FINALS: 'Quarter-final',
  SEMI_FINALS: 'Semi-final',
  FINAL: 'Final',
};
const RESULT = { 1: 'W', 0: 'D', '-1': 'L' };

const mq = window.matchMedia('(max-width: 720px)');
const subscribe = (cb) => (mq.addEventListener('change', cb), () => mq.removeEventListener('change', cb));
const useMobile = () => useSyncExternalStore(subscribe, () => mq.matches);

const when = (iso, opts) =>
  new Intl.DateTimeFormat('en-AU', { timeZone: site.tz, ...opts }).format(new Date(iso));
const kickOff = (iso) =>
  when(iso, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

const FOOTBALL_COLS = [
  { key: 'played', label: 'P', title: 'Played' },
  { key: 'gd', label: 'GD', title: 'Goal difference', signed: true },
  { key: 'pts', label: 'Pts', title: 'Points' },
];
const NRL_COLS = [
  { key: 'played', label: 'P', title: 'Played' },
  { key: 'gd', label: 'PD', title: 'Points difference', signed: true },
  { key: 'pts', label: 'Pts', title: 'Points' },
];
const F1_COLS = [
  { key: 'wins', label: 'W', title: 'Wins' },
  { key: 'pts', label: 'Pts', title: 'Points' },
];

function StandingsTable({ caption, data, cols, nameLabel, accent, mineLabel, renderName = (r) => r.name }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  if (!data) return <Status caption={caption}>Loading…</Status>;
  if (data.error || !data.rows?.length) return <Status caption={caption}>Couldn’t load this table right now.</Status>;

  // Collapsed: top 10, plus my team(s) if they sit lower down.
  const rows = data.rows.map((r, i) => ({ ...r, i }));
  const shown = open ? rows : rows.filter((r) => r.i < TOP || r.mine);

  return (
    <div className={`${styles.block} ${styles[accent]}`}>
      <table className={styles.table}>
        <caption className={styles.caption}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className={styles.pos}><abbr title="Position">#</abbr></th>
            <th scope="col">{nameLabel}</th>
            {cols.map((c) => (
              <th key={c.key} scope="col" className={styles.n}><abbr title={c.title}>{c.label}</abbr></th>
            ))}
          </tr>
        </thead>
        <tbody id={bodyId}>
          {shown.map((r, k) => (
            <Fragment key={r.i}>
              {!open && r.i >= TOP && shown[k - 1]?.i !== r.i - 1 && (
                <tr className={styles.gap} aria-hidden="true"><td colSpan={cols.length + 2}>⋯</td></tr>
              )}
              <tr className={r.mine ? styles.mine : undefined}>
                <td className={styles.pos}>{r.pos}</td>
                <th scope="row">
                  {renderName(r)}
                  {r.mine && <span className="sr-only"> ({mineLabel})</span>}
                </th>
                {cols.map((c) => (
                  <td key={c.key} className={styles.n}>{c.signed && r[c.key] > 0 ? `+${r[c.key]}` : r[c.key]}</td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
      {rows.length > TOP && (
        <button className={styles.more} aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(!open)}>
          {open ? `Show top ${TOP}` : `Show all ${rows.length}`} <span aria-hidden="true">{open ? '↑' : '↓'}</span>
        </button>
      )}
    </div>
  );
}

function Status({ caption, children }) {
  return (
    <div className={styles.block}>
      <p className={styles.caption}>{caption}</p>
      <p className={styles.status}>{children}</p>
    </div>
  );
}

const club = (r) => (
  <span className={styles.club}>
    {r.crest && <img src={r.crest} alt="" width="20" height="20" loading="lazy" />}
    {r.name}
  </span>
);
const driver = (r) => (
  <>
    {r.name} <span className={styles.sub}>{r.team}</span>
  </>
);

function Fixture({ m }) {
  const score = m.homeScore != null ? `${m.homeScore}–${m.awayScore}` : 'v';
  return (
    <>
      <span className={styles.sub}>{STAGES[m.stage] ?? m.stage} · {kickOff(m.date)}</span>
      <span className={styles.fixture}>
        {m.home} <b>{score}</b> {m.away}
        {m.pens && <span className={styles.sub}>({m.pens.home}–{m.pens.away} pens)</span>}
        {m.result != null && <span className={styles.badge} data-r={m.result}>{RESULT[m.result]}</span>}
      </span>
    </>
  );
}

function UclKnockout({ v }) {
  return (
    <div className={`${styles.block} ${styles.barca}`}>
      <p className={styles.caption}>Champions League · Knockouts</p>
      <div className={styles.headline}>
        {v.champions && <p>Champions of Europe.</p>}
        {v.out && <p>Out in the {STAGES[v.out.stage] ?? v.out.stage} against {v.out.by}.</p>}
        {v.next && (
          <>
            <p className={styles.label}>{v.next.status === 'IN_PLAY' || v.next.status === 'PAUSED' ? 'Playing now' : 'Next game'}</p>
            <Fixture m={v.next} />
          </>
        )}
        {!v.next && !v.out && !v.champions && <p>Through. Waiting on the draw for the next round.</p>}
      </div>
      {v.results.length > 0 && (
        <>
          <p className={styles.label}>Results</p>
          <ol className={styles.results}>
            {v.results.map((m) => (
              <li key={m.date}><Fixture m={m} /></li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function KnightsCard({ v }) {
  const m = v?.next ?? v?.last;
  if (!m) return null;
  const label = m.status === 'IN_PLAY' ? 'Playing now' : m === v.next ? 'Next game' : 'Last game';
  return (
    <div className={`${styles.block} ${styles.knights}`}>
      <p className={styles.caption}>Newcastle Knights</p>
      <div className={styles.headline}>
        <p className={styles.label}>{label}</p>
        <Fixture m={m} />
      </div>
    </div>
  );
}

export default function Passions() {
  const p = site.passions;
  const { data, failed } = useStandings();
  const isMobile = useMobile();
  const [tab, setTab] = useState(0);
  const tabRefs = useRef([]);
  const baseId = useId();

  const ucl = data?.ucl;
  const groups = [
    {
      label: p.football,
      body: (
        <>
          <StandingsTable caption="La Liga" data={data?.laliga} cols={FOOTBALL_COLS} nameLabel="Club" accent="barca" mineLabel="my club" renderName={club} />
          {ucl?.mode === 'knockout' ? (
            <UclKnockout v={ucl} />
          ) : (
            <StandingsTable caption="Champions League · League phase" data={ucl} cols={FOOTBALL_COLS} nameLabel="Club" accent="barca" mineLabel="my club" renderName={club} />
          )}
        </>
      ),
    },
    {
      label: p.f1,
      body: (
        <>
          <StandingsTable caption="Drivers’ championship" data={data?.drivers} cols={F1_COLS} nameLabel="Driver" accent="ferrari" mineLabel="my team" renderName={driver} />
          <StandingsTable caption="Constructors’ championship" data={data?.constructors} cols={F1_COLS} nameLabel="Team" accent="ferrari" mineLabel="my team" />
        </>
      ),
    },
    {
      label: p.nrl,
      body: (
        <>
          <StandingsTable caption="NRL Ladder" data={data?.nrl} cols={NRL_COLS} nameLabel="Club" accent="knights" mineLabel="my club" renderName={club} />
          <KnightsCard v={data?.nrl?.knights} />
        </>
      ),
    },
  ];

  const onKeyDown = (e) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
    if (!step) return;
    const next = (tab + step + groups.length) % groups.length;
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section className={styles.passions} id="passions">
      <header className="section-head">
        <span className="num" aria-hidden="true">{p.num}</span>
        <h2>{p.title}</h2>
        <p className="meta">{p.meta}</p>
      </header>

      {isMobile ? (
        <>
          <div role="tablist" aria-label="Sport" className={styles.tabs} onKeyDown={onKeyDown}>
            {groups.map((g, i) => (
              <button
                key={g.label}
                ref={(el) => (tabRefs.current[i] = el)}
                role="tab"
                id={`${baseId}-tab-${i}`}
                aria-controls={`${baseId}-panel-${i}`}
                aria-selected={tab === i}
                tabIndex={tab === i ? 0 : -1}
                onClick={() => setTab(i)}
              >
                {g.label}
              </button>
            ))}
          </div>
          {groups.map((g, i) => (
            <div key={g.label} role="tabpanel" id={`${baseId}-panel-${i}`} aria-labelledby={`${baseId}-tab-${i}`} hidden={tab !== i} className={styles.col}>
              {g.body}
            </div>
          ))}
        </>
      ) : (
        <Reveal className={styles.grid}>
          {groups.map((g) => (
            <div key={g.label} className={styles.col}>
              <h3 className={styles.groupTitle}>{g.label}</h3>
              {g.body}
            </div>
          ))}
        </Reveal>
      )}

      <p className={styles.updated}>
        {failed && !data && 'Live tables are taking a break. Check back soon.'}
        {data && (
          <>
            {data.live && <span className={styles.live}>Match / race day</span>}
            Updated {when(data.updated, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })} ·{' '}
            {data.live ? 'refreshing every minute' : 'refreshes hourly, every minute on match and race days'}
          </>
        )}
      </p>
    </section>
  );
}
